/**
 * Onboarding state the backend doesn't hold yet, kept per company in this browser's localStorage:
 * a demo booked in the dashboard's HubSpot iframe, shown only until
 * HubSpot's own copy comes through GET /Meeting/List (see resolveDemoBooking).
 * Every access is guarded: storage can be missing or throw (private mode, disabled site data).
 */

export interface DemoBooking {
    startISO: string | null;
    bookedAtISO: string;
}

export interface OnboardingStubState {
    /** A booking made in the dashboard's HubSpot iframe. */
    demoBooking?: DemoBooking;
}

export type StubStorage = Pick<Storage, 'getItem' | 'setItem'>;

export const onboardingStubKey = (companyKey: string) => `sc:onboarding:${companyKey}`;

const defaultStorage = (): StubStorage | null => {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
        return null;
    }
};

export const readOnboardingStub = (companyKey: string, storage: StubStorage | null = defaultStorage()): OnboardingStubState => {
    if (!storage) return {};
    try {
        const raw = storage.getItem(onboardingStubKey(companyKey));
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

export const writeOnboardingStub = (
    companyKey: string,
    patch: Partial<OnboardingStubState>,
    storage: StubStorage | null = defaultStorage(),
): OnboardingStubState => {
    const next = { ...readOnboardingStub(companyKey, storage), ...patch };
    if (storage) {
        try {
            storage.setItem(onboardingStubKey(companyKey), JSON.stringify(next));
        } catch {
            // storage full/disabled — the caller still gets the merged value for this render
        }
    }
    return next;
};
